import React, { useContext, useEffect, useMemo, useRef, useState } from 'react';
import Image from 'next/legacy/image';

import {
  flattenStrapiBulkDataItems,
  flattenStrapiDataItem,
} from '../../lib/flattenStrapiBulkDataItems';
import {
  NeighborhoodPageContext,
  NeighborhoodPageContextInterface,
} from '../../pages/[urlParam]';
import { getStrapiMedia } from '../../lib/getStrapiMedia';
import ErrorBoundary from '../ErrorBoundary';
import TableNeighborhood from './TableNeighborhood';
import Button from '../Button';
import LineBetweenSections from '../LineBetweenSections';
import { Metadata } from '../Metadata';
import Location from './Location';
import AboutNeighborhood from './AboutNeighborhoodSection/AboutNeighborhood';
import NeighborhoodCondos from './NeighborhoodCondos';
import NearbyNeighborhoods from './NearbyNeighborhoods';
import { createSearchUrlByType } from 'components/Search/utils/listingsFiltersUtils';
import Experts from 'components/Experts/Experts';
import { useExperts } from '../../hooks/useExperts';
import CommuteTimes from './CommuteTimes';
import MainFeatures from 'components/MainFeatures';

import { Coordinates } from 'apolloClient/types/Neighborhood';
import { getOriginalImageUrlFromImage } from 'lib/getImages';
import { defaultImage } from 'src/constants/constants';
import { getThreeUniquePageBlogs } from 'src/utils/blogs';
import { useVisibleSection } from '../../hooks/useVisibleSection';
import { PageLayout } from 'components/layouts/PageLayout';
import { RelatedArticlesSection } from '../RelatedArticlesSection';
import { NeighborhoodHeader } from './NeightorhoodPageHeader';
import { pageBuilderValidator } from 'src/utils/utils';
import ContentGroupTracker from 'src/gtm/ContentGroupTracker';

const NeighborhoodPage: React.FC = () => {
  const { neighborhood, url, globalBlogs } = useContext(
    NeighborhoodPageContext
  ) as NeighborhoodPageContextInterface;
  const { experts } = useExperts();
  const {
    id,
    name,
    image,
    slug,
    neighborhoodStats,
    blogs: blogsNeighborhood,
    neighborhoodsNeighbors,
    coordinates,
    city,
    commuteTimesLeft,
    commuteTimesRight,
    mainFeaturesCommon,
    walkScore,
    walkDescription,
    bikeScore,
    bikeDescription,
    transitScore,
    transitDescription,
  } = neighborhood;

  const mainFeaturesWalkscore = [
    {
      type: 'Walk_score',
      description:
        walkDescription && walkScore
          ? `${walkScore} (${walkDescription})`
          : null,
    },
    {
      type: 'Bike_score',
      description:
        bikeDescription && bikeScore
          ? `${bikeScore} (${bikeDescription})`
          : null,
    },
    {
      type: 'Transit_score',
      description:
        transitDescription && transitScore
          ? `${transitScore} (${transitDescription})`
          : null,
    },
  ];

  const cityName = city?.data?.attributes?.name;
  const title = `${name} Condos for Sale and Rent in ${cityName} | CondoBlackBook`;
  const description = `Search all ${name} condos in ${cityName}. Get real-time sale and rental availability with easy-to-use search, plus exclusive local insights, recent sales and latest news.`;

  const RentAndSaleRef = useRef<HTMLParagraphElement>(null);
  const MainFeaturesRef = useRef<HTMLParagraphElement>(null);
  const CommuteTimesRef = useRef<HTMLParagraphElement>(null);
  const LocationRef = useRef<HTMLParagraphElement>(null);
  const WelcomeToRef = useRef<HTMLParagraphElement>(null);
  const CondosRef = useRef<HTMLParagraphElement>(null);
  const ArticlesRef = useRef<HTMLParagraphElement>(null);
  const NeighborhoodsRef = useRef<HTMLParagraphElement>(null);

  const neighborhoodNameRef = useRef<HTMLDivElement>(null);

  const arrayBuildingBlogs = [
    ...flattenStrapiBulkDataItems(blogsNeighborhood?.data),
    ...globalBlogs,
  ];
  const threeUniquePageBlogs = getThreeUniquePageBlogs(arrayBuildingBlogs);

  const linksForHeader = useMemo(
    () => [
      {
        id: 'rentAndSale',
        name: 'For Sale & Rent',
        nameMobile: 'For Sale & Rent',
        section: 'rentAndSale',
        ref: RentAndSaleRef,
      },
      {
        id: 'mainFeatures',
        name: 'Highlights',
        nameMobile: 'Highlights',
        section: 'mainFeatures',
        ref: MainFeaturesRef,
        ignoreOffset: true,
      },
      // {
      //   id: 'mainFeatures',
      //   name: `${name} Highlights`,
      //   nameMobile: `${name} Highlights`,
      //   section: 'mainFeatures',
      //   ref: MainFeaturesRef,
      // },
      {
        id: 'location',
        name: 'Location',
        nameMobile: 'Location',
        section: 'location',
        ref: LocationRef,
        ignoreOffset: true,
      },
      {
        id: 'gettingAround',
        name: 'Getting Around',
        nameMobile: 'Getting Around',
        section: 'gettingAround',
        ref: CommuteTimesRef,
        ignoreOffset: true,
      },
      {
        id: 'welcomeTo',
        name: 'Welcome',
        nameMobile: 'Welcome',
        section: 'welcomeTo',
        ref: WelcomeToRef,
      },
      // {
      //   id: 'welcomeTo',
      //   name: `Welcome to ${name}`,
      //   nameMobile: `Welcome to ${
      //     name.length > 5 ? `${name.slice(0, 4)}...` : name
      //   }`,
      //   section: 'welcomeTo',
      //   ref: WelcomeToRef,
      // },
      {
        id: 'condos',
        name: 'Condos',
        nameMobile: 'Condos',
        section: 'condos',
        ref: CondosRef,
      },
      // {
      //   id: 'condos',
      //   name: `${name} Condos`,
      //   nameMobile: `${name} Condos`,
      //   section: 'condos',
      //   ref: CondosRef,
      // },
      // delete CBBRR-2677
      // {
      //   id: 'articles',
      //   name: `Related Articles`,
      //   nameMobile: `Related Articles`,
      //   section: 'articles',
      //   ref: ArticlesRef,
      // },
      // {
      //   id: 'neighborhoods',
      //   name: `Nearby Neighborhoods`,
      //   nameMobile: `Nearby Neighborhoods`,
      //   section: 'neighborhoods',
      //   ref: NeighborhoodsRef,
      // },
    ],
    []
  );

  const showMainFeatures =
    !!mainFeaturesCommon?.length ||
    mainFeaturesWalkscore.some((el) => el.description);
  const showCommuteTimes =
    !!commuteTimesLeft?.timeFirst ||
    !!commuteTimesLeft?.timeSecond ||
    !!commuteTimesRight?.length;

  const linksToRender = () => {
    let skipArr: string[] = [];
    !showMainFeatures && skipArr.push('mainFeatures');
    !showCommuteTimes && skipArr.push('gettingAround');
    !pageBuilderValidator(neighborhood.description).isDescriptionValid &&
      skipArr.push('welcomeTo');
    !isBuildings && skipArr.push('condos');
    return linksForHeader.filter((el) => !skipArr.includes(el.id));
  };

  const [isBuildings, setIsBuildings] = useState(false);
  const callbackBuildings = (value: any) => {
    setIsBuildings(value);
  };

  useEffect(() => {
    if ('scrollRestoration' in window.history) {
      window.history.scrollRestoration = 'manual';
    }

    return () => {
      window.history.scrollRestoration = 'auto';
    };
  }, []);

  const sectionRefs = [
    { id: 'rentAndSale', ref: RentAndSaleRef },
    { id: 'mainFeatures', ref: MainFeaturesRef },
    { id: 'location', ref: LocationRef },
    { id: 'welcomeTo', ref: WelcomeToRef },
    { id: 'gettingAround', ref: CommuteTimesRef },
    { id: 'condos', ref: CondosRef },
  ];

  const visibleSection = useVisibleSection(sectionRefs);

  const metadataProps = {
    title,
    image: getStrapiMedia(image?.data?.attributes, 'small'),
    description,
    url,
  };

  return (
    <>
      <ContentGroupTracker pageGroup={'neighborhoods'} />
      <Metadata {...metadataProps} />
      <main>
        <PageLayout
          title={name}
          links={linksToRender()}
          sectionScrollRef={RentAndSaleRef}
          visibleSection={visibleSection}
          breadcrumbs={{
            neighborhoodPage: true,
            neighborhoodName: name,
            neighborhoodId: slug,
          }}
          scrollPastRef={neighborhoodNameRef}
          headerContent={
            <NeighborhoodHeader
              name={name}
              neighborhoodNameRef={neighborhoodNameRef}
            />
          }
        >
          <section className="relative">
            {image?.data?.attributes?.url && (
              <div className="container mt-[21px] md:mt-0 md:pb-2 lg:py-2">
                <div className="relative h-[220px] md:h-[345px] xl:h-145">
                  <Image
                    layout="fill"
                    objectFit="cover"
                    src={getOriginalImageUrlFromImage(
                      flattenStrapiDataItem(image?.data)
                    )}
                    blurDataURL={defaultImage}
                    placeholder="blur"
                    loading="eager"
                    className="object-center w-full h-full"
                    alt={`${name} neighborhood`}
                    priority
                  />
                </div>
              </div>
            )}
          </section>
          <div className="relative md:pb-[69px]" ref={RentAndSaleRef}>
            <ErrorBoundary>
              <div className="relative">
                <div
                  id="rentAndSale"
                  ref={RentAndSaleRef}
                  className="absolute h-1 -top-40 md:-top-102 xl:-top-80"
                />
                <TableNeighborhood stats={neighborhoodStats} />
              </div>
            </ErrorBoundary>
            <div className="container pb-3 mt-[43px] md:mt-0 md:px-12 md:pt-5 md:pb-2 lg:pt-0 text-m">
              <div className="flex flex-col items-center justify-center mx-auto space-y-[21px] md:flex-row md:flex-wrap lg:flex-nowrap lg:space-y-0 lg:space-x-8 xl:space-x-[43px] lg:w-1/2">
                <Button
                  classNames="lg:pt-0 lg:!mt-[-23px] md:pb-0"
                  name={`${name} Condos for sale`}
                  href={createSearchUrlByType('sale', {
                    neighborhoods: [slug],
                  })}
                  classNameWidth="lg:pl-[19px]"
                  fontClassName="font-sohneBreitHalbfett md:pb-[1.5px] lg:pb-[1px]"
                />
                <Button
                  classNames="lg:pt-0 lg:!mt-[-23px] md:pb-0"
                  fontClassName="font-sohneBreitHalbfett md:pb-[1.5px] lg:pb-[1px]"
                  name={`${name} Condos for rent`}
                  href={createSearchUrlByType('rent', {
                    neighborhoods: [slug],
                  })}
                  classNameWidth="lg:pl-[19px]"
                />
              </div>
            </div>
          </div>
          {/* We will work on this section in phase 2 */}
          {showMainFeatures && (
            <section className="relative" ref={MainFeaturesRef}>
              <div
                id="mainFeatures"
                className="absolute -top-24 md:-top-44 lg:-top-72 xl:-top-80"
              />
              <MainFeatures
                nbhFeaturesWalkscore={mainFeaturesWalkscore}
                nbhFeaturesCommon={mainFeaturesCommon || []}
              />
            </section>
          )}
          <section className="relative" ref={LocationRef}>
            <div
              id="location"
              className="absolute -top-0 md:-top-60 lg:-top-28"
            />
            <ErrorBoundary>
              <Location
                coordinates={(coordinates as Coordinates) || []}
                neighborhoodId={id}
              />
            </ErrorBoundary>
          </section>
          {showCommuteTimes && (
            <section className="relative" ref={CommuteTimesRef}>
              <div
                id="gettingAround"
                className="absolute -top-24 md:-top-96 lg:-top-72"
              />
              <CommuteTimes
                commuteTimesLeft={commuteTimesLeft}
                commuteTimesRight={commuteTimesRight}
              />
            </section>
          )}
          <section className="relative" ref={WelcomeToRef}>
            <div
              id="welcomeTo"
              className="absolute -top-24 md:-top-32 lg:-top-28"
            />
            <ErrorBoundary>
              <AboutNeighborhood neighborhood={neighborhood} />
            </ErrorBoundary>
          </section>
          <LineBetweenSections hideLine={'container'} />
          <section className="relative" ref={CondosRef}>
            <div
              id="condos"
              className="absolute -top-24 md:-top-32 lg:-top-28"
            />
            <ErrorBoundary>
              <NeighborhoodCondos
                callbackBuildings={callbackBuildings}
                neigborhood={neighborhood}
              />
            </ErrorBoundary>
          </section>
          <section className="relative">
            <Experts
              experts={experts}
              flagLabelTextSize={'text-16-21-0.53 bg-beige'}
              expertCardImageWidth="xl:w-[140px] md:w-[146px] w-[143px]"
              expertCardImageHeight="xl:h-[177px] md:h-[184px] h-[120px]"
            />
          </section>
          <LineBetweenSections hideLine={'container'} />
          <section className="relative" ref={ArticlesRef}>
            <div
              id="articles"
              className="absolute -top-24 md:-top-32 lg:-top-28"
            />
            <ErrorBoundary>
              <RelatedArticlesSection
                blogs={threeUniquePageBlogs}
                showButtonMore="mb-[67px]"
              />
            </ErrorBoundary>
          </section>
          <section className="relative overflow-hidden" ref={NeighborhoodsRef}>
            <div
              id="neighborhoods"
              className="absolute -top-24 md:-top-32 lg:-top-28"
            />
            <ErrorBoundary>
              <NearbyNeighborhoods
                neighborhoods={neighborhoodsNeighbors?.data}
              />
            </ErrorBoundary>
          </section>
        </PageLayout>
      </main>
    </>
  );
};

export default NeighborhoodPage;
